import { useCallback, useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { Copy, Loader2, ShieldCheck, ShieldOff, Smartphone } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Chip, Loading, Panel, PrimaryButtonClass } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";

export const MfaSelfService = () => {
  const [status, setStatus] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/auth/mfa/status");
    setStatus(data);
  }, []);

  useEffect(() => { load().catch(() => setStatus(false)); }, [load]);

  const start = async () => {
    setBusy("start");
    try {
      const { data } = await api.post("/auth/mfa/enroll/start");
      setEnrollment(data);
      setCodes([]);
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const confirm = async () => {
    setBusy("confirm");
    try {
      const { data } = await api.post("/auth/mfa/enroll/confirm", { code: code.trim() });
      setCodes(data.recovery_codes || []);
      setEnrollment(null);
      setCode("");
      toast.success("Two-factor authentication is now active on your account");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const disable = async () => {
    setBusy("disable");
    try {
      await api.post("/auth/mfa/disable");
      toast.success("Two-factor authentication switched off");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  if (status === null) return <Loading label="Checking your security posture" />;
  if (status === false) return null;

  return (
    <Panel testId="panel-my-security" kicker="My account" title="Two-factor authentication">
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Chip testId="my-mfa-state"
                className={status.enrolled
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                  : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"}>
            {status.enrolled ? "active" : "not enrolled"}
          </Chip>
          {status.enforced && (
            <Chip className="border-primary/30 bg-primary/10 text-primary" testId="my-mfa-enforced">
              required by admin
            </Chip>
          )}
          {status.enrolled && (
            <span className="text-xs text-muted-foreground">
              enrolled {dateTime(status.enrolled_at)} · {status.recovery_codes_remaining} recovery codes left
            </span>
          )}
        </div>

        {!status.enrolled && !enrollment && (
          <Button onClick={start} disabled={busy === "start"} data-testid="my-mfa-start-button" className={PrimaryButtonClass}>
            {busy === "start" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Smartphone className="mr-1.5 h-4 w-4" />}
            Set up authenticator
          </Button>
        )}

        {enrollment && (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start" data-testid="my-mfa-enrollment">
            <div className="rounded-xl bg-white p-3">
              <QRCode value={enrollment.provisioning_uri} size={132} />
            </div>
            <div className="flex-1 space-y-3">
              <button type="button" data-testid="my-mfa-secret"
                      onClick={() => { navigator.clipboard?.writeText(enrollment.secret); toast.success("Secret copied"); }}
                      className="inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground hover:text-primary">
                <Copy className="h-3 w-3" /> {enrollment.secret}
              </button>
              <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} inputMode="numeric"
                     placeholder="123456" data-testid="my-mfa-code-input"
                     className="max-w-[180px] bg-muted/40 text-center font-mono tracking-[0.3em]" />
              <Button onClick={confirm} disabled={busy === "confirm" || code.trim().length !== 6}
                      data-testid="my-mfa-confirm-button" className={PrimaryButtonClass}>
                {busy === "confirm" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Activate
              </Button>
            </div>
          </div>
        )}

        {codes.length > 0 && (
          <div className="rounded-xl border border-border bg-muted/40 p-3" data-testid="my-mfa-recovery-codes">
            <div className="mb-2 text-xs font-semibold text-foreground">Recovery codes — store them now</div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {codes.map((c) => (
                <span key={c} className="rounded-lg bg-card px-2 py-1.5 text-center font-mono text-[12px] text-foreground">{c}</span>
              ))}
            </div>
          </div>
        )}

        {status.enrolled && !status.enforced && (
          <Button variant="outline" onClick={disable} disabled={busy === "disable"}
                  data-testid="my-mfa-disable-button" className="rounded-full">
            {busy === "disable" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldOff className="mr-1.5 h-4 w-4" />}
            Turn off two-factor
          </Button>
        )}
        {status.enrolled && status.enforced && (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="my-mfa-locked-note">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            Your administrator requires two-factor on this account, so it cannot be switched off here.
          </p>
        )}
      </div>
    </Panel>
  );
};
